function Privacy() {
  return (
    <div className="animate-fade-in mx-auto max-w-4xl py-12">
      <h1 className="gradient-text mb-4 text-4xl font-bold">
        Privacy Policy for Xhalin
      </h1>

      <p className="mb-2 text-gray-600">
        We built Xhalin to help you breathe easier, not to invade your privacy.
        This page explains what we collect, why we collect it, and how we handle
        your data.
      </p>
      <p className="mb-6 text-sm text-gray-500">Last updated: November 2025</p>

      <div className="space-y-6">
        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">
            1. Information We Collect
          </h2>
          <p className="text-gray-700">
            We only collect the information we genuinely need to run the app,
            improve it, and keep your account working smoothly.
          </p>

          <h3 className="mb-2 mt-4 font-medium">1.1 Information You Provide</h3>
          <ul className="list-inside list-disc text-gray-700">
            <li>
              <strong>Account Information:</strong> Email address and password
              when you create an account.
            </li>
            <li>
              <strong>Breathing Session Data:</strong> Your ratios, durations,
              templates, background choices, and saved sessions.
            </li>
            <li>
              <strong>Profile Settings:</strong> Preferences like reminders,
              breathing reminders, and media settings.
            </li>
            <li>
              <strong>Communication:</strong> Messages sent to support or
              feedback channels.
            </li>
          </ul>

          <h3 className="mb-2 mt-4 font-medium">
            1.2 Information Collected Automatically
          </h3>
          <ul className="list-inside list-disc text-gray-700">
            <li>Device details like browser, OS, and screen size</li>
            <li>Log data such as IP address, timestamps, and pages viewed</li>
            <li>Cookies for login, preferences, and minimal analytics</li>
          </ul>

          <h3 className="mb-2 mt-4 font-medium">1.3 Third-Party Services</h3>
          <p className="text-gray-700">
            We may use Supabase for authentication, database storage, and user
            profiles; analytics tools to understand how the site is used; and
            push notification services if you enable reminders.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">
            2. How We Use Your Information
          </h2>
          <p className="text-gray-700">We use your data to:</p>
          <ul className="list-inside list-disc text-gray-700">
            <li>Provide and personalize your breathing experience</li>
            <li>Save sessions, templates, and progress</li>
            <li>Show optional public leaderboard rankings</li>
            <li>Improve performance and user experience</li>
            <li>Send reminders or optional updates</li>
            <li>Protect the platform from abuse or fraud</li>
          </ul>
          <div className="mt-4 rounded-lg bg-gray-50 p-4 text-gray-800">
            We never sell your personal information. Ever.
          </div>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">3. Data Storage</h2>
          <p className="text-gray-700">
            All data is stored securely through Supabase using encrypted storage
            and HTTPS. You can request full deletion of your account and data at
            any time.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">4. Cookies</h2>
          <p className="text-gray-700">
            We use minimal cookies for authentication, saving your app
            preferences, and basic analytics. You may disable cookies, but some
            features will stop working.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">5. Public Leaderboard</h2>
          <p className="text-gray-700">
            If you choose to save your session publicly, your username and total
            time may appear on the leaderboard. Private sessions are never
            shared.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">6. Third-Party Links</h2>
          <p className="text-gray-700">
            Xhalin may include links to external websites. We are not
            responsible for their privacy practices or content.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">7. Children’s Privacy</h2>
          <p className="text-gray-700">
            Xhalin is not intended for users under the age of 13, and we do not
            knowingly collect data from children.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">8. Your Rights</h2>
          <ul className="list-inside list-disc text-gray-700">
            <li>Access your data</li>
            <li>Update profile information</li>
            <li>Export your data</li>
            <li>Delete your account and all data</li>
            <li>Disable reminders or emails</li>
          </ul>
          <p className="mt-3 text-gray-700">
            Contact us anytime at <strong>support@xhalin.com</strong>.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">9. Data Retention</h2>
          <p className="text-gray-700">
            We keep your data only as long as needed to provide the service or
            meet legal requirements. Deleting your account permanently removes
            your data.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">10. Security</h2>
          <p className="text-gray-700">
            We use encrypted storage, secure authentication, and modern security
            practices. While no system is perfect, we take your safety
            seriously.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">
            11. Updates to This Policy
          </h2>
          <p className="text-gray-700">
            If we make changes, we’ll update the date at the top and notify
            users when necessary.
          </p>
        </section>

        <section className="card">
          <h2 className="mb-3 text-2xl font-semibold">12. Contact Us</h2>
          <p className="text-gray-700">
            If you have any questions, reach out at{' '}
            <strong>support@xhalin.com</strong>.
          </p>
        </section>
      </div>
    </div>
  );
}

export default Privacy;
